import { useContext, useLayoutEffect } from 'react';

import * as am5 from "@amcharts/amcharts5";
import * as am5radar from "@amcharts/amcharts5/radar";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";
import { DataContext } from 'contexts/DataContext';

interface RiskProps {
  name: string;
  value: number;
  height?: string;
}


const RiskChart = (props: RiskProps) => {
  const dataContext = useContext(DataContext);
  const isDark = !!dataContext.darkMode;

  useLayoutEffect(() => {
    const root = am5.Root.new(props.name);
    root._licenseApplied();

    // Tema: copia dal vecchio progetto in dark, solo Animated in light
    root.setThemes(
      isDark
        ? [am5themes_Animated.new(root), am5themes_Dark.new(root)]
        : [am5themes_Animated.new(root)]
    );

    // Chart
    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        startAngle: 160,
        endAngle: 380,
      })
    );

    // Axis + renderer (innerRadius come vecchio progetto)
    const axisRenderer = am5radar.AxisRendererCircular.new(root, {
      innerRadius: -25,
      minGridDistance: 50,
      visible: false,
    });

    axisRenderer.grid.template.setAll({
      stroke: root.interfaceColors.get("background"),
      visible: false,
      strokeOpacity: 0.8,
    });

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0,
        min: 0,
        max: 5,
        strictMinMax: true,
        renderer: axisRenderer,
      })
    );

    // Lancetta
    const axisDataItem = xAxis.makeDataItem({});

    const clockHand = am5radar.ClockHand.new(root, {
      pinRadius: am5.percent(20),
      radius: am5.percent(95),
      bottomWidth: 35,
    });

    const bullet = axisDataItem.set(
      "bullet",
      am5xy.AxisBullet.new(root, {
        sprite: clockHand,
      })
    );

    xAxis.createAxisRange(axisDataItem);

    const label = chart.radarContainer.children.push(
      am5.Label.new(root, {
        fill: am5.color(isDark ? 0xffffff : 0x000000),
        centerX: am5.percent(50),
        textAlign: "center",
        centerY: am5.percent(50),
        fontSize: "1em",
      })
    );

    axisDataItem.set("value", props.value);

    bullet.get("sprite").on("rotation", () => {
      const value = axisDataItem.get("value");
      let fill = am5.color(0x000000);
      const val = value ?? 0;

      // Come nel vecchio progetto: il colore della lancetta segue la banda
      xAxis.axisRanges.each((axisRange) => {
        const startValue = axisRange.get("value") ?? 0;
        const endValue = axisRange.get("endValue") ?? 0;
        if (val >= startValue && val <= endValue) {
          fill = axisRange?.get("axisFill")?.get("fill") ?? am5.color(0x000000);
        }
      });

      label.set("text", value?.toString());
      label.set("fill", am5.color(isDark ? 0xffffff : 0x000000));

      clockHand.pin.animate({
        key: "fill",
        to: fill,
        duration: 500,
        easing: am5.ease.out(am5.ease.cubic),
      });
      clockHand.hand.animate({
        key: "fill",
        to: fill,
        duration: 500,
        easing: am5.ease.out(am5.ease.cubic),
      });
    });

    setInterval(() => {
      axisDataItem.animate({
        key: "value",
        to: props.value,
        duration: 500,
        easing: am5.ease.out(am5.ease.cubic),
      });
    }, 5000);

    chart.bulletsContainer.set("mask", undefined);

    // Bande come vecchio progetto
    const bandsData = [
      {
        title: "Low",
        color: "#2CC937",
        lowScore: 0,
        highScore: 1,
      },
      {
        title: "Medium/Low",
        color: "#99C140",
        lowScore: 1,
        highScore: 2,
      },
      {
        title: "Medium",
        color: "#E7B417",
        lowScore: 2,
        highScore: 3,
      },
      {
        title: "Medium/High",
        color: "#DB7B2A",
        lowScore: 3,
        highScore: 4,
      },
      {
        title: "High",
        color: "#CC3232",
        lowScore: 4,
        highScore: 5,
      },
    ];

    am5.array.each(bandsData, (data) => {
      const axisRange = xAxis.createAxisRange(xAxis.makeDataItem({}));

      axisRange.setAll({
        value: data.lowScore,
        endValue: data.highScore,
      });

      axisRange?.get("axisFill")?.setAll({
        visible: true,
        fill: am5.color(data.color),
        fillOpacity: isDark ? 0.8 : 1,
      });

      axisRange?.get("label")?.setAll({
        text: data.title,
        inside: true,
        fontSize: "0.5em",
        fill: am5.color(0xffffff),
      });
    });

    chart.appear(1000, 100);

    // Exporting: mantiene comportamento, solo label nera quando esporti
    const exporting = am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
      backgroundColor: am5.color("#fff"),
      backgroundOpacity: 1,
      filePrefix: "GlobalRisk",
    });

    exporting.events.on("exportstarted", () => {
      root.setThemes([]);
      label.set("fill", am5.color("#000"));
    });

    exporting.events.on("exportfinished", () => {
      root.setThemes(
        isDark
          ? [am5themes_Animated.new(root), am5themes_Dark.new(root)]
          : [am5themes_Animated.new(root)]
      );
      label.set("fill", am5.color(isDark ? 0xffffff : 0x000000));
    });

    return () => {
      root.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, isDark]);

  return <div id={props.name} style={{ width: "100%", height: props.height ?? "300px" }}></div>;
};

export default RiskChart;